import { Link } from '@tanstack/react-router'
import type { DetailedUser } from '../types'

import { AvatarWithInitials } from '@/components/common/avatar-with-initials'
import { PromoteUserButton } from '@/features/direct-reports'

interface UserProfileHeaderProps {
  user: DetailedUser
  onPromote?: () => void
  showActions?: boolean
}

/**
 * User Profile Header Component
 *
 * Displays user information with avatar, name, position, manager, and organization
 * Includes action buttons for managing the user
 *
 * @param user - DetailedUser object with all user information
 * @param onEditRoles - Callback for edit roles button
 * @param onChangeManager - Callback for change manager button
 * @param onPromote - Callback for promote button
 * @param onDelete - Callback for delete button
 * @param showActions - Whether to show action buttons (default: true)
 */
export const UserProfileHeader = ({
  user,
  onPromote,
  showActions = true,
}: UserProfileHeaderProps) => {
  return (
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 bg-muted/30 rounded-lg">
      {/* Left side: Avatar and Info */}
      <div className="flex items-start gap-4">
        <AvatarWithInitials
          name={user.name}
          src={user.avatar}
          className="h-16 w-16"
          fallbackClassName="text-lg"
        />

        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-foreground">
            {user.name}
          </h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {/* Position */}
            {user.currentUserPosition?.positionTrajectory.fromPosition && (
              <>
                <span className="font-medium">
                  {user.currentUserPosition.positionTrajectory.fromPosition.title}
                </span>
                <span>|</span>
              </>
            )}

            {/* Manager */}
            {user.manager && (
              <>
                <span>Managed by</span>
                <Link
                  className="font-medium text-primary"
                  to="/direct-reports/$directReportId/career-path"
                  params={{ directReportId: user.manager.id }}
                  search={{
                    name: user.manager.name || undefined,
                  }}
                >
                  {user.manager.name}
                </Link>
                <span>|</span>
              </>
            )}

            {/* Organization */}
            {user.organization && (
              <span className="font-medium">{user.organization.name}</span>
            )}
          </div>
        </div>
      </div>

      {/* Right side: Action Buttons */}
      {showActions && (
        <div className="flex flex-wrap items-center gap-2">
          <PromoteUserButton
            userId={user.id}
            userName={user.name}
            currentPosition={
              user.currentUserPosition?.positionTrajectory.fromPosition
                ? {
                    role: user.currentUserPosition.positionTrajectory.fromPosition
                      .role,
                    level:
                      user.currentUserPosition.positionTrajectory.fromPosition.level,
                    title:
                      user.currentUserPosition.positionTrajectory.fromPosition.title,
                    sinceDate: user.currentUserPosition.sinceDate,
                  }
                : null
            }
            onSuccess={(_data) => onPromote?.()}
          />
        </div>
      )}
    </div>
  )
}
